/**
 * Shared helpers for the voice-qa developer-timeline card. Extracted
 * from developer_transcript_timeline.tsx so the UI component stays
 * focused on layout while pure logic (entry classification, advisor
 * payload parsing, time-offset math) is independently testable.
 */

import { z } from 'zod';

import { formatBucketSlug } from '@apps/web/routes/_app/admin/voice-qa/bucket_formatting';

export type DeveloperTimelineEntry = {
    readonly sequence: number;
    readonly timestamp_iso?: string | null;
    readonly pts_ns?: number | null;
    readonly kind: string;
    readonly role?: string;
    readonly text?: string;
    // Flattened from assistant_spoken_progress payloads: the words the
    // TTS has spoken so far within the segment described by `text`.
    readonly accumulated_text_preview?: string;
    readonly tool_call_id?: string;
    readonly function_name?: string;
    readonly arguments?: Record<string, unknown> | null;
    readonly result?: Record<string, unknown> | null;
    readonly metadata?: Record<string, unknown> | null;
    readonly function_calls?: ReadonlyArray<Record<string, unknown>> | null;
    readonly payload?: Record<string, unknown> | null;
};

/** The advisor observer writes its notes onto the developer timeline
 * with this kind and the advice payload attached. See
 * services/voice/advisor_agent/advisor.py.
 */
export const ADVISOR_AGENT_EVENT_KIND = 'advisor_agent_note';

const TTS_EVENT_KINDS = new Set([
    'tts_audio_missing',
    'tts_audio_interrupted',
    'tts_disconnected',
    'tts_connected',
]);

export type TimelineLane =
    | 'conversation'
    | 'tool'
    | 'advisor'
    | 'tts_event'
    | 'other';

export function classifyEntry(entry: DeveloperTimelineEntry): TimelineLane {
    if (entry.kind === 'assistant_text' || entry.kind === 'user_text') {
        return 'conversation';
    }
    if (entry.kind.startsWith('tool_')) {
        return 'tool';
    }
    if (entry.kind === ADVISOR_AGENT_EVENT_KIND) {
        return 'advisor';
    }
    if (TTS_EVENT_KINDS.has(entry.kind)) {
        return 'tts_event';
    }
    return 'other';
}

export function formatOffsetSeconds(
    totalSeconds: number | null | undefined,
): string {
    if (totalSeconds === null || totalSeconds === undefined) {
        return '—';
    }
    const rounded = Math.max(0, Math.floor(totalSeconds));
    const minutes = Math.floor(rounded / 60);
    const seconds = rounded % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Compute the relative time offset (seconds) for an entry.
 *
 * Prefers the nanosecond presentation timestamp stamped by the voice
 * pipeline when present; falls back to wall-clock ISO deltas against
 * the first entry that carried a timestamp.
 *
 * For ``tts_audio_missing`` entries the voice pipeline stamps
 * ``timestamp_iso`` at the moment the pending chunk is *finalized*
 * (which can be many seconds after the chunk was requested — e.g. on
 * pipeline teardown). The payload carries ``elapsed_ms`` =
 * finalization − request, so we subtract it to anchor the event at
 * the time the TTS was actually attempted. That way an interrupted
 * chunk appears next to the agent turn it belonged to, not at the
 * point where the pipeline swept it up.
 */
export function getEntryOffsetSeconds(
    entry: DeveloperTimelineEntry,
    firstTimestampMs: number | null,
): number | null {
    if (typeof entry.pts_ns === 'number') {
        return entry.pts_ns / 1_000_000_000;
    }
    if (!entry.timestamp_iso) {
        return null;
    }
    const timeMs = Date.parse(entry.timestamp_iso);
    if (Number.isNaN(timeMs) || firstTimestampMs === null) {
        return null;
    }
    const adjustedMs = timeMs - _ttsElapsedMs(entry);
    return Math.max(0, Math.round((adjustedMs - firstTimestampMs) / 1000));
}

function _ttsElapsedMs(entry: DeveloperTimelineEntry): number {
    if (entry.kind !== 'tts_audio_missing') {
        return 0;
    }
    const metadata = entry.metadata;
    if (!metadata || typeof metadata !== 'object') {
        return 0;
    }
    const raw = (metadata as Record<string, unknown>)['elapsed_ms'];
    if (typeof raw !== 'number' || !Number.isFinite(raw) || raw <= 0) {
        return 0;
    }
    return raw;
}

export function getTimelineBaselineMs(
    sortedEntries: ReadonlyArray<DeveloperTimelineEntry>,
): number | null {
    // Return the wall-clock (ISO) baseline, independent of whether
    // pts_ns is present on any row. pts_ns is already call-relative
    // (nanoseconds since stream start), so entries using it don't
    // need a baseline — but ISO-only rows (advisor_agent_note,
    // assistant_text, etc. appended later) still do. Previously
    // returning 0 when the first row had pts_ns meant ISO-only rows
    // computed `Date.parse(timestamp) - 0`, rendering huge
    // epoch-scale offsets on mixed timelines.
    for (const entry of sortedEntries) {
        if (!entry.timestamp_iso) {
            continue;
        }
        const ms = Date.parse(entry.timestamp_iso);
        if (!Number.isNaN(ms)) {
            return ms;
        }
    }
    return null;
}

/**
 * Shape the voice-qa UI expects from an advisor_agent_note entry.
 * Advisor observer stashes these fields on `payload` in new emissions
 * and on `metadata` in older ones; unify both here.
 */
export type ParsedAdvisorNote = {
    readonly adviceType: string | null;
    readonly reason: string | null;
    readonly bucket: string | null;
    readonly secondaryBucket: string | null;
    readonly suggestedActionType: string | null;
    readonly evidence: ReadonlyArray<string>;
};

export function parseAdvisorPayload(
    entry: DeveloperTimelineEntry,
): ParsedAdvisorNote {
    const raw = (entry.payload ?? entry.metadata ?? {}) as Record<
        string,
        unknown
    >;
    const stringField = (key: string): string | null =>
        typeof raw[key] === 'string' && (raw[key] as string).trim() !== ''
            ? (raw[key] as string)
            : null;
    const evidenceList = Array.isArray(raw['evidence'])
        ? (raw['evidence'] as ReadonlyArray<unknown>).filter(
              (item): item is string =>
                  typeof item === 'string' && item.trim() !== '',
          )
        : [];
    return {
        adviceType: stringField('type'),
        reason: stringField('reason'),
        bucket: stringField('bucket'),
        secondaryBucket: stringField('secondary_bucket'),
        suggestedActionType: stringField('suggested_action_type'),
        evidence: evidenceList,
    };
}

export function formatSlug(value: string): string {
    return formatBucketSlug(value);
}

/**
 * Short human-readable label for a TTS event kind, used on the
 * compact single-line TTS row.
 */
export function formatTtsEventKind(kind: string): string {
    switch (kind) {
        case 'tts_audio_missing':
            return 'TTS chunk interrupted';
        case 'tts_audio_interrupted':
            return 'TTS interrupted';
        case 'tts_disconnected':
            return 'TTS disconnected';
        case 'tts_connected':
            return 'TTS connected';
        default:
            return formatBucketSlug(kind);
    }
}

export function extractTtsPreview(
    entry: DeveloperTimelineEntry,
): string | null {
    const metadata = entry.metadata;
    if (!metadata || typeof metadata !== 'object') {
        return null;
    }
    const preview = (metadata as Record<string, unknown>)['text_preview'];
    if (typeof preview === 'string' && preview.trim() !== '') {
        return preview.length > 120 ? `${preview.slice(0, 117)}…` : preview;
    }
    return null;
}

/**
 * A display-ready entry in the developer timeline. For most entries
 * this is just the underlying ``DeveloperTimelineEntry`` verbatim; TTS
 * events can optionally carry a ``count`` when duplicates have been
 * coalesced, so the row can render an "× N" badge.
 */
export type DisplayTimelineEntry = {
    readonly entry: DeveloperTimelineEntry;
    readonly count: number;
};

/**
 * Collapse duplicate TTS events into a single display entry.
 *
 * The voice pipeline emits one ``tts_audio_missing`` per pending
 * chunk, so a single interrupted agent utterance can produce a cloud
 * of near-identical events (same kind + same ``text_preview``). We
 * fold entries that share ``(kind, text_preview)`` into a single row,
 * anchored on the first occurrence, carrying a count for the rest.
 * Non-TTS entries pass through unchanged.
 */
export function coalesceTimelineEntries(
    entries: ReadonlyArray<DeveloperTimelineEntry>,
): Array<DisplayTimelineEntry> {
    const result: Array<DisplayTimelineEntry> = [];
    for (const entry of entries) {
        const lane = classifyEntry(entry);
        if (lane !== 'tts_event') {
            result.push({ entry, count: 1 });
            continue;
        }
        const preview = extractTtsPreview(entry) ?? '';
        const lastEntry =
            result.length > 0 ? result[result.length - 1] : undefined;
        const lastPreview =
            lastEntry !== undefined
                ? (extractTtsPreview(lastEntry.entry) ?? '')
                : null;
        if (
            lastEntry !== undefined &&
            classifyEntry(lastEntry.entry) === 'tts_event' &&
            lastEntry.entry.kind === entry.kind &&
            lastPreview === preview
        ) {
            result[result.length - 1] = {
                entry: lastEntry.entry,
                count: lastEntry.count + 1,
            };
            continue;
        }
        result.push({ entry, count: 1 });
    }
    return result;
}

/**
 * Count visible entries per lane so the header summary + filter
 * toggles can show accurate totals without re-traversing the list.
 */
export function countEntriesByLane(
    entries: ReadonlyArray<DeveloperTimelineEntry>,
): Record<TimelineLane, number> {
    const counts: Record<TimelineLane, number> = {
        conversation: 0,
        tool: 0,
        advisor: 0,
        tts_event: 0,
        other: 0,
    };
    for (const entry of entries) {
        counts[classifyEntry(entry)] += 1;
    }
    return counts;
}

const timelineRecord = z.record(z.string(), z.unknown());
const providerName = z.string().trim().min(1);
const speechSelectionEvent = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('voice_speech_runtime_selected'),
        stt_provider: providerName,
    }),
    z.object({
        kind: z.literal('voice_tts_runtime_selected'),
        targets: z.array(
            z.object({
                target: z.string(),
                service: providerName,
                model: providerName.nullish(),
            }),
        ),
    }),
    z.object({
        kind: z.literal('tts_provider_per_call_fallback_selected'),
        to_service: providerName,
        to_model: providerName.nullish(),
    }),
]);

/** Read canonical flattened selections, with metadata fallback for older events. */
export function getSpeechProviderSelections(
    entries: ReadonlyArray<unknown> | null | undefined,
) {
    const stt = new Set<string>();
    const tts = new Set<string>();
    const ttsFallback = new Set<string>();
    for (const entry of entries ?? []) {
        const record = timelineRecord.safeParse(entry);
        if (
            !record.success ||
            !speechSelectionEvent.options.some(
                (schema) => schema.shape.kind.value === record.data['kind'],
            )
        ) {
            continue;
        }
        const metadata = timelineRecord.safeParse(record.data['metadata']);
        const parsed = speechSelectionEvent.safeParse({
            ...(metadata.success ? metadata.data : {}),
            ...record.data,
        });
        if (!parsed.success) {
            continue;
        }
        const event = parsed.data;
        switch (event.kind) {
            case 'voice_speech_runtime_selected':
                stt.add(event.stt_provider);
                break;
            case 'voice_tts_runtime_selected':
                // A configured backup is not evidence that it was selected.
                for (const target of event.targets) {
                    if (target.target === 'primary') {
                        tts.add(
                            target.model
                                ? `${target.service}/${target.model}`
                                : target.service,
                        );
                    }
                }
                break;
            case 'tts_provider_per_call_fallback_selected':
                ttsFallback.add(
                    event.to_model
                        ? `${event.to_service}/${event.to_model}`
                        : event.to_service,
                );
                break;
            default:
                event satisfies never;
        }
    }
    return { stt: [...stt], tts: [...tts], ttsFallback: [...ttsFallback] };
}
